import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  exhaustMap,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { ProjectLocationPlanService } from '../../services/project-location-plan.service';
import { ProjectLocationPlanActions } from '../state/project-location-plan/project-location-plan.actions';
import { Router } from '@angular/router';
import { routerSegments } from '../../consts/routes';
import { Store } from '@ngrx/store';
import { projectFeature } from '../state/project/project.feature';
import {
  ConfirmDialogModel,
  ConfirmDialogService,
  MsgBoxType,
  ToastService,
} from '@ui';
import { TranslateService } from '@ngx-translate/core';

export const loadProjectLocationPlans = createEffect(
  (
    actions$ = inject(Actions),
    planService = inject(ProjectLocationPlanService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.loadProjectLocationPlans),
      switchMap(({ projectId }) => {
        return planService.getAll(projectId).pipe(
          map((plans) =>
            ProjectLocationPlanActions.loadProjectLocationPlansSuccess({
              plans,
            }),
          ),
          catchError(() =>
            of(ProjectLocationPlanActions.loadProjectLocationPlansFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const createProjectLocationPlan = createEffect(
  (
    actions$ = inject(Actions),
    planService = inject(ProjectLocationPlanService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.createProjectLocationPlan),
      exhaustMap(({ projectId, locationId, plan }) => {
        return planService.create(projectId, locationId, plan).pipe(
          map((createdPlan) =>
            ProjectLocationPlanActions.createProjectLocationPlanSuccess({
              plan: createdPlan,
            }),
          ),
          catchError(() =>
            of(ProjectLocationPlanActions.createProjectLocationPlanFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const loadSelectedPlan = createEffect(
  (
    actions$ = inject(Actions),
    planService = inject(ProjectLocationPlanService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.loadSelectedPlan),
      switchMap(({ planId }) => {
        return planService.getSelectedPlan(planId).pipe(
          map((plan) =>
            ProjectLocationPlanActions.loadSelectedPlanSuccess({
              plan,
            }),
          ),
          catchError(() =>
            of(ProjectLocationPlanActions.loadSelectedPlanFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const startCopyPlan = createEffect(
  (
    actions$ = inject(Actions),
    planService = inject(ProjectLocationPlanService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.startCopyPlan),
      switchMap(({ planId }) => {
        return planService.copyPlan(planId).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant(
                'PROJECT_PLAN_VIEW.PLAN_COPY_SUCCESS',
              ) as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((plan) =>
            ProjectLocationPlanActions.startCopyPlanSuccess({ plan }),
          ),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant(
                'PROJECT_PLAN_VIEW.PLAN_COPY_FAIL',
              ) as string,
              MsgBoxType.ERROR,
            );
            return of(ProjectLocationPlanActions.startCopyPlanFail());
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const editProjectLocationPlan = createEffect(
  (
    actions$ = inject(Actions),
    planService = inject(ProjectLocationPlanService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.editProjectLocationPlan),
      switchMap(({ planId, data }) => {
        return planService.savePlan(planId, data).pipe(
          map((plan) =>
            ProjectLocationPlanActions.editProjectLocationPlanSuccess({ plan }),
          ),
          catchError(() =>
            of(ProjectLocationPlanActions.editProjectLocationPlanFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const requestDeletePlan = createEffect(
  (
    actions$ = inject(Actions),
    confirmDialogService = inject(ConfirmDialogService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.requestDeletePlan),
      concatMap(({ planId }) => {
        return confirmDialogService
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: 'EDIT_PLAN_MODAL.DELETE_CONFIRM.TITLE',
            msg: 'EDIT_PLAN_MODAL.DELETE_CONFIRM.MESSAGE',
            confirm: 'EDIT_PLAN_MODAL.DELETE_PLAN',
            cancel: 'COMMON.CANCEL',
            disableClose: true,
          })
          .pipe(
            map((result) =>
              result?.selection === ConfirmDialogModel.ConfirmResult.OK
                ? ProjectLocationPlanActions.startDeletePlan({ planId })
                : ProjectLocationPlanActions.requestDeletePlanCancel(),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const startDeletePlan = createEffect(
  (
    actions$ = inject(Actions),
    planService = inject(ProjectLocationPlanService),
    router = inject(Router),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.startDeletePlan),
      withLatestFrom(store.select(projectFeature.selectSelectedProject)),
      switchMap(([{ planId }, project]) => {
        return planService.deletePlan(planId).pipe(
          map(() => {
            if (project) {
              void router.navigate([
                routerSegments.projects,
                routerSegments.view,
                project._id,
              ]);
            }
            return ProjectLocationPlanActions.startDeletePlanSuccess();
          }),
          catchError(() =>
            of(ProjectLocationPlanActions.startDeletePlanFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const uploadProjectLocationPlanFloorImage = createEffect(
  (
    actions$ = inject(Actions),
    planService = inject(ProjectLocationPlanService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.uploadProjectLocationPlanFloorImage),
      switchMap(({ planId, uploadDto }) =>
        planService.uploadImage(planId, uploadDto.filename).pipe(
          switchMap(({ url, filename }) =>
            planService
              .uploadAttachmentToSignedUrl({
                url,
                file: uploadDto.file,
                filename,
              })
              .pipe(map(() => ({ filename }))),
          ),

          switchMap(({ filename }) =>
            planService.savePlan(planId, {
              floorBounds: uploadDto.floorBounds,
              floorScale: uploadDto.floorScale,
              floorImage: filename,
            }),
          ),

          switchMap((plan) =>
            of(
              ProjectLocationPlanActions.editProjectLocationPlanSuccess({
                plan,
              }),
              ProjectLocationPlanActions.downloadPlanPictureSuccess({
                image: uploadDto.file,
              }),
              ProjectLocationPlanActions.uploadProjectLocationPlanFloorImageSuccess(
                { plan },
              ),
            ),
          ),

          catchError(() =>
            of(
              ProjectLocationPlanActions.uploadProjectLocationPlanFloorImageFail(
                { planId },
              ),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const downloadPlanPicture = createEffect(
  (
    actions$ = inject(Actions),
    planService = inject(ProjectLocationPlanService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanActions.downloadPlanPicture),
      switchMap(({ floorImageUrl }) => {
        return planService.storageDownload(floorImageUrl).pipe(
          map((image) => {
            return ProjectLocationPlanActions.downloadPlanPictureSuccess({
              image,
            });
          }),
          catchError(() =>
            of(ProjectLocationPlanActions.downloadPlanPictureFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);
